/**
 * Favorites, remembered.
 *
 * A star that forgets itself on reload is a decoration. This is a per-browser
 * convenience rather than shared state — one person's shortlist is not the
 * desk's — so it lives in localStorage and every access is guarded: a private
 * window, cleared site data or a browser refusing storage must leave the page
 * working with no favorites, not throw on first render.
 *
 * The key is the one the previous Reporting page used, and so are the ids it
 * stored (a saved report's id, a schedule's id, a generated file's id), so a
 * reader's stars survive the rebuild.
 */
import type { LibraryReport } from './library'

export const FAVORITES_KEY = 'reporting.favorites.v1'

export function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

export function writeFavorites(ids: string[]) {
  try {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids))
  } catch {
    // Storage is unavailable; the stars still work for this session.
  }
}

/** Every id a report may have been starred under, old and new. */
export function favoriteIdsOf(report: Pick<LibraryReport, 'key' | 'definition' | 'schedules' | 'runs'>): string[] {
  return [
    report.key,
    ...(report.definition ? [report.definition.id] : []),
    ...report.schedules.map((schedule) => schedule.id),
    ...report.runs.map((run) => run.id),
  ]
}

/** The id a new star is stored under: the saved report's own, else the library key. */
export function favoriteIdToStore(report: Pick<LibraryReport, 'key' | 'definition'>): string {
  return report.definition?.id ?? report.key
}

export function isFavorite(
  report: Pick<LibraryReport, 'key' | 'definition' | 'schedules' | 'runs'>,
  favorites: ReadonlySet<string>,
): boolean {
  return favoriteIdsOf(report).some((id) => favorites.has(id))
}
